import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { imgUrl } from "@/api";
import { Lightbox } from "./Lightbox";

export const Gallery = ({ products, categories }) => {
  const [filter, setFilter] = useState("tumu");
  const [index, setIndex] = useState(null);

  const filtered = useMemo(
    () => (filter === "tumu" ? products : products.filter((p) => p.category === filter)),
    [products, filter]
  );

  const openProduct = (p) => {
    const i = filtered.findIndex((x) => x.id === p.id);
    setIndex(i >= 0 ? i : null);
  };

  return (
    <section id="koleksiyon" className="py-24 md:py-36" data-testid="gallery-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 md:mb-20"
        >
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-stone-500 mb-4">Koleksiyon</p>
          <h2 className="font-serif font-light tracking-tight text-stone-800 text-3xl sm:text-4xl lg:text-5xl max-w-xl">
            Her parça bir hikâye taşır
          </h2>
        </motion.div>

        <div className="flex flex-wrap gap-3 mb-12 md:mb-16" data-testid="gallery-filters">
          <button
            onClick={() => setFilter("tumu")}
            data-testid="gallery-filter-tumu"
            className={`px-5 py-2.5 text-xs font-semibold tracking-[0.15em] uppercase border transition-colors duration-300 ${
              filter === "tumu"
                ? "bg-stone-900 text-stone-50 border-stone-900"
                : "border-stone-300 text-stone-600 hover:border-stone-800 hover:text-stone-900"
            }`}
          >
            Tümü
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.slug)}
              data-testid={`gallery-filter-${c.slug}`}
              className={`px-5 py-2.5 text-xs font-semibold tracking-[0.15em] uppercase border transition-colors duration-300 ${
                filter === c.slug
                  ? "bg-stone-900 text-stone-50 border-stone-900"
                  : "border-stone-300 text-stone-600 hover:border-stone-800 hover:text-stone-900"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-10" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (i % 4) * 0.06 }}
                className="group cursor-pointer"
                onClick={() => openProduct(p)}
                data-cursor="Görüntüle"
                data-testid={`gallery-item-${p.id}`}
              >
                <div className="overflow-hidden bg-beige-soft aspect-[4/5]">
                  <img
                    src={imgUrl(p.image_url)}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-3">
                  <h3 className="font-serif text-xl text-stone-800 tracking-tight">{p.title}</h3>
                  <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-stone-400">
                    {categories.find((c) => c.slug === p.category)?.name || p.category}
                  </span>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <p className="text-stone-500 font-light py-20 text-center" data-testid="gallery-empty">
            Bu kategoride henüz ürün yok.
          </p>
        )}
      </div>

      <Lightbox items={filtered} index={index} onClose={() => setIndex(null)} onNavigate={setIndex} />
    </section>
  );
};
